import { AdminUser, Course, CourseWithUnits, DashboardCourse, DeckFlashcard, Exam, ExamAttempt, FlashcardDeckSummary, Lesson, ManagedUserActivity, MeResponse, Organisation, OrganisationClass, OrganisationExamProgressDetail, OrganisationLessonProgressDetail, OrganisationManagement, OrganisationProgressClass, OrganisationProgressItem, OrganisationProgressReport, OrganisationProgressSummary, OrganisationRole, OrganisationStudentProgress, ProgressItemType, ProgressScope, RecentActivity } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8500';

const adminJson = async <T>(response: Response, fallback: string): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({} as any));
    throw new Error(error?.detail || fallback);
  }
  return response.json();
};

const progressScopePath = (itemType: ProgressItemType, itemId: string, scope: ProgressScope) => {
  const itemPath = `/organisation/progress/${itemType}/${encodeURIComponent(itemId)}`;
  return scope === 'all' ? `${itemPath}/all` : `${itemPath}/classes/${scope}`;
};

export const api = {
  // --- Courses ---
  async getCourses(): Promise<{ courses: Course[] }> {
    const response = await fetch(`${API_BASE_URL}/courses`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch courses');
    }
    return response.json();
  },

  async getAdminCourses(): Promise<{ courses: Course[] }> {
    const response = await fetch(`${API_BASE_URL}/admin/courses`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch admin courses');
    }
    return response.json();
  },

  async getCourse(courseId: number): Promise<CourseWithUnits> {
    const response = await fetch(`${API_BASE_URL}/courses/${courseId}`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch course');
    }
    return response.json();
  },

  async createCourse(payload: { title: string; description?: string; detailed_description?: string; subjects?: string[]; levels?: string[]; tags?: string[]; hidden?: boolean }) {
    const response = await fetch(`${API_BASE_URL}/admin/courses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to create course');
    }
    return response.json();
  },

  async updateCourse(courseId: number, payload: { title?: string; description?: string; detailed_description?: string; subjects?: string[]; levels?: string[]; tags?: string[]; hidden?: boolean }) {
    const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to update course');
    }
    return response.json();
  },

  async deleteCourse(courseId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete course');
    }
    return response.json();
  },

  // --- Units ---
  async createUnit(courseId: number, name: string, color?: string | null) {
    const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}/units`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, color })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to create unit');
    }
    return response.json();
  },

  async updateUnit(unitId: number, payload: { name?: string; color?: string | null; order_index?: number }) {
    const response = await fetch(`${API_BASE_URL}/admin/units/${unitId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to update unit');
    }
    return response.json();
  },

  async deleteUnit(unitId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/units/${unitId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to delete unit');
    }
    return response.json();
  },

  async reorderUnit(unitId: number, direction: 'up' | 'down') {
    const response = await fetch(`${API_BASE_URL}/admin/units/${unitId}/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ direction })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to reorder unit');
    }
    return response.json();
  },

  async moveLessonToUnit(lessonId: string, unitId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/lessons/${encodeURIComponent(lessonId)}/move-to-unit`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ unit_id: unitId })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to move lesson');
    }
    return response.json();
  },

  async updateLessonOrder(lessonId: string, orderIndex: number) {
    const response = await fetch(`${API_BASE_URL}/admin/lessons/${encodeURIComponent(lessonId)}/order`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ order_index: orderIndex })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to update lesson order');
    }
    return response.json();
  },

  // --- External Links ---
  async createExternalLink(unitId: number, name: string, url: string) {
    const response = await fetch(`${API_BASE_URL}/admin/units/${unitId}/external-links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, url })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to create external link');
    }
    return response.json();
  },

  async updateExternalLink(linkId: number, payload: { name?: string; url?: string; order_index?: number }) {
    const response = await fetch(`${API_BASE_URL}/admin/external-links/${linkId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to update external link');
    }
    return response.json();
  },

  async deleteExternalLink(linkId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/external-links/${linkId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to delete external link');
    }
    return response.json();
  },

  // --- Exams ---
  async getExam(examId: string): Promise<Exam> {
    const response = await fetch(`${API_BASE_URL}/exams/${encodeURIComponent(examId)}`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch exam');
    }
    return response.json();
  },

  async getExamCourse(examId: string): Promise<{ course_id: number }> {
    const response = await fetch(`${API_BASE_URL}/exams/${encodeURIComponent(examId)}/course`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch exam course');
    }
    return response.json();
  },

  async createExam(unitId: number, payload: { id?: string; name: string; time?: number | null; data: Exam['data'] }) {
    const response = await fetch(`${API_BASE_URL}/admin/units/${unitId}/exams`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to create exam');
    }
    return response.json();
  },

  async updateExam(examId: string, payload: { name?: string; time?: number | null; data?: Exam['data']; order_index?: number }) {
    const response = await fetch(`${API_BASE_URL}/admin/exams/${encodeURIComponent(examId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to update exam');
    }
    return response.json();
  },

  async deleteExam(examId: string) {
    const response = await fetch(`${API_BASE_URL}/admin/exams/${encodeURIComponent(examId)}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to delete exam');
    }
    return response.json();
  },

  async getExamAttempts(examId: string): Promise<{ attempts: ExamAttempt[] }> {
    const response = await fetch(`${API_BASE_URL}/exams/${encodeURIComponent(examId)}/attempts`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch exam attempts');
    }
    return response.json();
  },

  async getExamAttempt(attemptId: string): Promise<ExamAttempt> {
    const response = await fetch(`${API_BASE_URL}/exam-attempts/${encodeURIComponent(attemptId)}`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch exam attempt');
    }
    return response.json();
  },

  async saveExamAttempt(attemptId: string, payload: { exam_id: string; data: { answers: Record<number, string | string[]>; remaining_time_seconds?: number | null } }) {
    const response = await fetch(`${API_BASE_URL}/exam-attempts/${encodeURIComponent(attemptId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to save exam attempt');
    }
    return response.json();
  },

  async submitExamAttempt(attemptId: string, payload: { exam_id: string; data: { answers: Record<number, string | string[]>; remaining_time_seconds?: number | null } }) {
    const response = await fetch(`${API_BASE_URL}/exam-attempts/${encodeURIComponent(attemptId)}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to submit exam attempt');
    }
    return response.json();
  },

  // --- Asset Materials ---
  assetDownloadUrl(assetId: number): string {
    return `${API_BASE_URL}/assets/${assetId}`;
  },

  async createAssetMaterial(unitId: number, name: string, assetId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/units/${unitId}/asset-materials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, asset_id: assetId })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to create asset material');
    }
    return response.json();
  },

  async updateAssetMaterial(materialId: number, payload: { name?: string; asset_id?: number; order_index?: number }) {
    const response = await fetch(`${API_BASE_URL}/admin/asset-materials/${materialId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to update asset material');
    }
    return response.json();
  },

  async deleteAssetMaterial(materialId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/asset-materials/${materialId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to delete asset material');
    }
    return response.json();
  },

  async getCourseAssets(courseId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}/assets`, {
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch course assets');
    }
    return response.json();
  },

  // --- Lessons ---
  async getLessons() {
    const response = await fetch(`${API_BASE_URL}/lessons`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch lessons');
    }
    return response.json();
  },

  async getLesson(lessonId: string) {
    const response = await fetch(`${API_BASE_URL}/lessons/${encodeURIComponent(lessonId)}`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch lesson');
    }
    return response.json();
  },

  async getLessonCourse(lessonId: string): Promise<{ course_id: number }> {
    const response = await fetch(`${API_BASE_URL}/lessons/${encodeURIComponent(lessonId)}/course`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch lesson course');
    }
    return response.json();
  },
  

  async deleteLesson(lessonId: string) {
    const response = await fetch(`${API_BASE_URL}/lessons/${encodeURIComponent(lessonId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to delete lesson');
    }
    return response.json();
  },


  async moveLesson(lessonId: string, courseId: number) {
    const response = await fetch(`${API_BASE_URL}/admin/lessons/${encodeURIComponent(lessonId)}/move`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ course_id: courseId })
    });
    if (!response.ok) throw new Error('Failed to move lesson');
    return response.json();
  },

  async getLessonChats(lessonId: string) {
    const response = await fetch(`${API_BASE_URL}/lessons/${encodeURIComponent(lessonId)}/chats`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch lesson chats');
    }
    return response.json();
  },

  async rateLessonChat(chatId: string, rating: 'up' | 'down'): Promise<{ success: boolean; chat_id: string; rating: 'up' | 'down' }> {
    const response = await fetch(`${API_BASE_URL}/chats/${encodeURIComponent(chatId)}/rating`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ rating }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to rate lesson');
    }
    return response.json();
  },

  async getFlashcardDeckSummary(): Promise<FlashcardDeckSummary> {
    const response = await fetch(`${API_BASE_URL}/flashcards/summary`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch flashcard deck summary');
    }
    return response.json();
  },

  async getDeckFlashcards(courseId?: number): Promise<{ flashcards: DeckFlashcard[] }> {
    const query = courseId == null ? '' : `?course_id=${encodeURIComponent(courseId)}`;
    const response = await fetch(`${API_BASE_URL}/flashcards${query}`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch flashcard deck');
    }
    return response.json();
  },

  async getRevisionFlashcards(courseId?: number): Promise<{ flashcards: DeckFlashcard[] }> {
    const query = courseId == null ? '' : `?course_id=${encodeURIComponent(courseId)}`;
    const response = await fetch(`${API_BASE_URL}/revise${query}`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch revision flashcards');
    }
    return response.json();
  },

  async addCourseFlashcardsToDeck(courseId: number): Promise<{ added_count: number; total_count: number; deck_count: number; [key: string]: unknown }> {
    const response = await fetch(`${API_BASE_URL}/courses/${courseId}/flashcards/deck`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to add flashcards to deck');
    }
    return response.json();
  },

  async recordFlashcardResponse(flashcardId: number, responseValue: 'wrong' | 'tough' | 'easy'): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/flashcards/${flashcardId}/responses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ response: responseValue })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to record flashcard response');
    }
    return response.json();
  },

  async getLessonChatsAdmin(lessonId: string) {
    const response = await fetch(`${API_BASE_URL}/admin/lessons/${encodeURIComponent(lessonId)}/chats`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch lesson chats (admin)');
    }
    return response.json();
  },

  async setLessonVisibility(lessonId: string, isPublic: boolean) {
    const response = await fetch(`${API_BASE_URL}/lessons/${encodeURIComponent(lessonId)}/visibility`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_public: isPublic }),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to update lesson visibility');
    }
    return response.json();
  },

  // --- Dashboard ---
  async getDashboard(): Promise<{ courses: DashboardCourse[]; latest_activity: RecentActivity | null; flashcard_count: number }> {
    const response = await fetch(`${API_BASE_URL}/dashboard`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch dashboard');
    }
    return response.json();
  },

  async submitFeedback(feedback: string): Promise<{ success: boolean; feedback_id: number }> {
    const response = await fetch(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ feedback }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to submit feedback');
    }
    return response.json();
  },

  // --- My Courses ---
  async getMyCourses(): Promise<{ course_ids: number[] }> {
    const response = await fetch(`${API_BASE_URL}/my-courses`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch my courses');
    }
    return response.json();
  },

  async addMyCourse(courseId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/my-courses/${courseId}`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to add course to my courses');
    }
    return response.json();
  },

  async removeMyCourse(courseId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/my-courses/${courseId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to remove course from my courses');
    }
    return response.json();
  },

  // --- Auth ---
  async login(email: string, password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Login failed');
    }
    return response.json();
  },

  async register(email: string, password: string, is_admin: boolean = false) {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, is_admin }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Registration failed');
    }
    return response.json();
  },

  async verifyEmail(token: string) {
    const response = await fetch(
      `${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`,
      { credentials: 'include' }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Email verification failed');
    }
    return response.json();
  },

  async resendVerification(email: string) {
    const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Failed to resend verification email');
    }
    return response.json();
  },

  async forgotPassword(email: string) {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Failed to request a password reset');
    }
    return response.json();
  },

  async createOrganisationUsers(organisationId: number, emails: string[], organisationRole: 'STUDENT' | 'TEACHER' = 'STUDENT', classIds: number[] = []): Promise<{
    results: { email: string; status: 'created' | 'resent' | 'skipped' | 'failed' | 'email_failed' }[];
  }> {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/users`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails, organisation_role: organisationRole, class_ids: classIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Failed to create users');
    }
    return response.json();
  },

  async activateAccount(token: string, password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/activate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Failed to activate account');
    }
    return response.json();
  },

  async resetPassword(token: string, password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Failed to reset password');
    }
    return response.json();
  },

  async loginWithGoogle(credential: string) {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.detail || 'Google sign-in failed');
    }
    return response.json();
  },

  async logout() {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    return response.json();
  },

  async me(): Promise<MeResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to get session');
    return response.json();
  },

  // --- API Key ---
  async getApiKey(): Promise<{ success: boolean; api_key: string | null }> {
    const response = await fetch(`${API_BASE_URL}/auth/api-key`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch API key');
    }
    return response.json();
  },

  async generateApiKey(): Promise<{ success: boolean; api_key: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/api-key`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to generate API key');
    }
    return response.json();
  },

  // --- Admin: Users ---
  async getUsers(): Promise<{ users: AdminUser[] }> {
    const response = await fetch(`${API_BASE_URL}/admin/users`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    return response.json();
  },

  async setUserAdmin(userId: number, isAdmin: boolean) {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/admin`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_admin: isAdmin }),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to update user admin');
    }
    return response.json();
  },

  // --- Admin: Organisations ---
  async getOrganisations(): Promise<{ organisations: Organisation[] }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisations`, {
      credentials: 'include',
    });
    return adminJson(response, 'Failed to fetch organisations');
  },

  async getOrganisation(organisationId: number): Promise<{ organisation: Organisation & { classes: OrganisationClass[] } }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisations/${organisationId}`, {
      credentials: 'include',
    });
    return adminJson(response, 'Failed to fetch organisation');
  },

  async createOrganisation(payload: { name: string; allow_public_courses: boolean }): Promise<{ success: boolean; organisation: Organisation }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    return adminJson(response, 'Failed to create organisation');
  },

  async updateOrganisation(organisationId: number, payload: { name?: string; allow_public_courses?: boolean }): Promise<{ success: boolean; organisation: Organisation }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisations/${organisationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    return adminJson(response, 'Failed to update organisation');
  },

  async deleteOrganisation(organisationId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisations/${organisationId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return adminJson(response, 'Failed to delete organisation');
  },

  async createOrganisationClass(organisationId: number, name: string): Promise<{ success: boolean; class: OrganisationClass }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisations/${organisationId}/classes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });
    return adminJson(response, 'Failed to create class');
  },

  async updateOrganisationClass(classId: number, name: string): Promise<{ success: boolean; class: OrganisationClass }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisation-classes/${classId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });
    return adminJson(response, 'Failed to update class');
  },

  async deleteOrganisationClass(classId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisation-classes/${classId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return adminJson(response, 'Failed to delete class');
  },

  async setUserOrganisation(
    userId: number,
    organisationId: number | null,
    organisationRole: OrganisationRole | null,
  ): Promise<{ success: boolean; user: AdminUser }> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/organisation`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        organisation_id: organisationId,
        organisation_role: organisationRole,
      }),
    });
    return adminJson(response, 'Failed to update user organisation');
  },

  async addClassUser(classId: number, userId: number): Promise<{ success: boolean; class: OrganisationClass }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisation-classes/${classId}/users/${userId}`, {
      method: 'PUT',
      credentials: 'include',
    });
    return adminJson(response, 'Failed to add user to class');
  },

  async removeClassUser(classId: number, userId: number): Promise<{ success: boolean; class: OrganisationClass }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisation-classes/${classId}/users/${userId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return adminJson(response, 'Failed to remove user from class');
  },

  async grantClassCourse(classId: number, courseId: number): Promise<{ success: boolean; class: OrganisationClass }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisation-classes/${classId}/courses/${courseId}`, {
      method: 'PUT',
      credentials: 'include',
    });
    return adminJson(response, 'Failed to grant course access');
  },

  async revokeClassCourse(classId: number, courseId: number): Promise<{ success: boolean; class: OrganisationClass }> {
    const response = await fetch(`${API_BASE_URL}/admin/organisation-classes/${classId}/courses/${courseId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return adminJson(response, 'Failed to revoke course access');
  },

  // --- Organisation management ---
  async getOrganisationManagement(organisationId: number): Promise<OrganisationManagement> {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/management`, {
      credentials: 'include',
    });
    return adminJson(response, 'Failed to load organisation management');
  },

  async getManagedUserActivity(userId: number): Promise<ManagedUserActivity> {
    const response = await fetch(`${API_BASE_URL}/manage/users/${userId}`, {
      credentials: 'include',
    });
    return adminJson(response, 'Failed to load user activity');
  },

  async getManagedUserActivityDetail(
    userId: number,
    activityType: 'chat' | 'exam',
    activityId: string,
  ): Promise<OrganisationLessonProgressDetail | OrganisationExamProgressDetail> {
    const response = await fetch(
      `${API_BASE_URL}/manage/users/${userId}/activity/${activityType}/${encodeURIComponent(activityId)}`,
      { credentials: 'include' },
    );
    return adminJson(response, 'Failed to load activity preview');
  },

  // --- Organisation: Student progress ---
  async getOrganisationProgressClasses(
    itemType: ProgressItemType,
    itemId: string,
  ): Promise<{
    item: OrganisationProgressItem;
    classes: OrganisationProgressClass[];
    can_view_all_users: boolean;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/organisation/progress/${itemType}/${encodeURIComponent(itemId)}/classes`,
      { credentials: 'include' },
    );
    return adminJson(response, 'Failed to load available classes');
  },

  async getOrganisationClassProgress(
    itemType: ProgressItemType,
    itemId: string,
    scope: ProgressScope,
  ): Promise<{
    item: OrganisationProgressItem;
    class: OrganisationProgressClass | null;
    students: OrganisationStudentProgress[];
    summary: OrganisationProgressSummary;
  }> {
    const response = await fetch(
      `${API_BASE_URL}${progressScopePath(itemType, itemId, scope)}`,
      { credentials: 'include' },
    );
    return adminJson(response, 'Failed to load class progress');
  },

  async getOrganisationClassProgressReport(
    itemType: ProgressItemType,
    itemId: string,
    classId: number,
  ): Promise<{ report: OrganisationProgressReport | null }> {
    const response = await fetch(
      `${API_BASE_URL}/organisation/progress/${itemType}/${encodeURIComponent(itemId)}/classes/${classId}/report`,
      { credentials: 'include' },
    );
    return adminJson(response, 'Failed to load report');
  },

  async generateOrganisationClassProgressReport(
    itemType: ProgressItemType,
    itemId: string,
    classId: number,
  ): Promise<{ report: OrganisationProgressReport }> {
    const response = await fetch(
      `${API_BASE_URL}/organisation/progress/${itemType}/${encodeURIComponent(itemId)}/classes/${classId}/report`,
      { method: 'POST', credentials: 'include' },
    );
    return adminJson(response, 'Failed to generate report');
  },

  async getOrganisationStudentProgressDetail(
    itemType: ProgressItemType,
    itemId: string,
    scope: ProgressScope,
    studentUserId: number,
  ): Promise<OrganisationLessonProgressDetail | OrganisationExamProgressDetail> {
    const response = await fetch(
      `${API_BASE_URL}${progressScopePath(itemType, itemId, scope)}/students/${studentUserId}`,
      { credentials: 'include' },
    );
    return adminJson(response, 'Failed to load student progress');
  },

  // --- Admin: Email ---
  async getAdminEmailAudiences(): Promise<{ all_users: number; verified_users: number }> {
    const response = await fetch(`${API_BASE_URL}/admin/email/audiences`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to load email audiences');
    }
    return response.json();
  },

  async sendAdminEmail(payload: {
    audience: 'all' | 'verified' | 'specific';
    specific_email?: string;
    from_email: string;
    subject: string;
    text_body: string;
    html_body: string;
  }): Promise<{
    success: boolean;
    recipient_count: number;
    sent_count: number;
    failed_count: number;
    failed_recipients: string[];
  }> {
    const response = await fetch(`${API_BASE_URL}/admin/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to send email');
    }
    return response.json();
  },

  // --- Realtime dictation ---
  async createRealtimeTranscriptionToken(
    prompt?: string,
  ): Promise<{ client_secret: string; expires_at: number | null }> {
    const response = await fetch(
      `${API_BASE_URL}/realtime-transcription-token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Could not start dictation');
    }
    return response.json();
  },

  // --- Admin: Prompt Tester ---
  async generatePromptSamples(payload: { context: any; overrides?: { system_prompt?: string; parameters?: any }; num_samples?: number; }) {
    const response = await fetch(`${API_BASE_URL}/admin/prompt-tester/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to generate samples');
    }
    return response.json();
  },

  // --- Admin: Export context for a specific assistant message ---
  async getAssistantContext(chatId: string, assistantIndex: number) {
    const response = await fetch(`${API_BASE_URL}/admin/chats/${encodeURIComponent(chatId)}/assistant/${assistantIndex}/context`, {
      credentials: 'include'
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err?.detail || 'Failed to fetch assistant context');
    }
    return response.json();
  }
};

export const fetchCourses = async (): Promise<Course[]> => {
  const response = await api.getCourses();
  return response.courses;
};

export const fetchLessons = async (): Promise<Lesson[]> => {
  const response = await api.getLessons();
  return response.lessons;
};

export const fetchUsers = async () => {
  const response = await api.getUsers();
  return response.users;
};
